// A thumbnail grid of photos. Each image loads via an authenticated office proxy (by index) so the
// raw Drive URL is never exposed; clicking opens the full-size image in a new tab. Shared by the
// service-record panel, the site service-history expand (SiteDetailPage), the project photo
// section (ProjectPhotos) and the site photos section. When `identify` is set, each thumbnail
// offers an "Identify this part" action (Session AP) that shows the AI best-guess below the grid.
//
// Session ADE additions, both opt-in so the read-only surfaces are untouched:
//  - `onReorder`: the grid becomes drag-sortable (@dnd-kit, the platform's one drag library);
//    a drop reports the full new order (a permutation of the current indices) for the caller to
//    persist. A small pointer-distance activation keeps plain clicks (open, delete, identify)
//    working exactly as before -- a reorder gesture never swallows them, and vice versa.
//  - The per-photo delete is TWO-STEP (the platform rule for a permanent delete): the first
//    click arms the control ("Delete?"), the second deletes; it disarms on blur, Escape or a
//    short timeout, mirroring the shared ConfirmButton.
import { useEffect, useRef, useState } from "react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, useSortable, rectSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api } from "../../lib/api";
import { photoOrderFromDrag } from "../../lib/photoDrag";
import { PartIdentify } from "./PartIdentify";

export function JobPhotoGallery({
  jobId,
  count,
  label = "Photos",
  emptyText,
  onDeletePhoto,
  version = 0,
  buildPhotoUrl,
  altLabel,
  identify,
  onReorder,
}: {
  jobId: string;
  count: number;
  label?: string;
  // When set, an empty job shows this placeholder under the heading; when omitted, an
  // empty job renders nothing at all (the history expand only mounts this with photos).
  emptyText?: string;
  // When provided (office job-sheet view), each thumbnail shows a delete control. Omitted
  // on the read-only site service-history view.
  onDeletePhoto?: (index: number) => void;
  // Bumped by the parent after a delete or reorder: photos are addressed by index and the proxy
  // sets a short cache, so a change shifts the indices and would otherwise show a stale cached
  // thumbnail. A version > 0 appends a cache-busting query param so the grid refetches.
  version?: number;
  // Override the per-index image URL. Defaults to the job photo proxy; the project photo
  // section passes the project proxy so this gallery is reusable across both surfaces.
  buildPhotoUrl?: (index: number) => string;
  // Override the per-image alt / link text. Defaults to "Job photo N".
  altLabel?: (index: number) => string;
  // When set, each thumbnail offers an "Identify this part" action wired to this source + id.
  identify?: { source: "jobsheet" | "project"; id: string };
  // When set, the grid is drag-sortable and a drop reports the new order for persisting.
  onReorder?: (order: number[]) => void;
}) {
  const [activeIdentify, setActiveIdentify] = useState<number | null>(null);
  // The armed (first-click) state of the two-step per-photo delete, by index.
  const [armedDelete, setArmedDelete] = useState<number | null>(null);
  // Indices whose thumbnail image failed to load: those tiles render a branded "Photo unavailable"
  // placeholder instead of a broken image (Session AEH, FIX-2 B3).
  const [failedIndexes, setFailedIndexes] = useState<Set<number>>(new Set());
  const disarmTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (disarmTimer.current) clearTimeout(disarmTimer.current);
    },
    [],
  );
  // Any change to the photo set disarms a stale confirm and clears stale load failures (photos are
  // index-addressed, so a version bump reshuffles which URL each tile loads).
  useEffect(() => {
    setArmedDelete(null);
    setFailedIndexes(new Set());
  }, [count, version]);

  // A small movement threshold so a plain click (open / delete / identify) never starts a drag,
  // and a drag never fires the click handlers underneath it.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  // A completed pointer drag still leaves a synthetic click on the element under the pointer;
  // suppress it so dropping a photo never also opens it full-size (or arms its delete).
  const justDragged = useRef(false);
  function markDragged() {
    justDragged.current = true;
    setTimeout(() => {
      justDragged.current = false;
    }, 0);
  }

  if (count <= 0 && !emptyText) return null;
  const bust = version > 0 ? `?v=${version}` : "";
  const photoUrl = buildPhotoUrl ?? ((i: number) => api.jobs.photoUrl(jobId, i));
  const altOf = altLabel ?? ((i: number) => `Job photo ${i + 1}`);
  const sortable = onReorder != null && count > 1;

  function disarm() {
    setArmedDelete(null);
    if (disarmTimer.current) {
      clearTimeout(disarmTimer.current);
      disarmTimer.current = null;
    }
  }

  function deleteClick(i: number) {
    if (!onDeletePhoto) return;
    if (armedDelete === i) {
      disarm();
      onDeletePhoto(i);
      return;
    }
    setArmedDelete(i);
    if (disarmTimer.current) clearTimeout(disarmTimer.current);
    // Auto-reset after a few seconds so a forgotten armed state never lingers as a live delete.
    disarmTimer.current = setTimeout(() => setArmedDelete(null), 4000);
  }

  function handleDragEnd(e: DragEndEvent) {
    markDragged();
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const order = photoOrderFromDrag(count, String(active.id), String(over.id));
    if (order) onReorder?.(order);
  }

  const tiles = Array.from({ length: count }, (_, i) => {
    const url = `${photoUrl(i)}${bust}`;
    const active = identify != null && activeIdentify === i;
    const armed = armedDelete === i;
    const failed = failedIndexes.has(i);
    const tileInner = (
      <>
        {failed ? (
          <div className="flex h-full w-full items-center justify-center bg-ele-inset-strong p-2 text-center" title="This photo could not be loaded">
            {/* [overflow-wrap:anywhere]: a three-column tile on a 320px phone is about 64px inside its padding, narrower than "UNAVAILABLE" (Session BDY). */}
            <span className="max-w-full font-menu text-[10px] uppercase tracking-wide text-ele-body opacity-70 [overflow-wrap:anywhere]">Photo unavailable</span>
          </div>
        ) : (
          <a href={url} target="_blank" rel="noopener noreferrer" className="block h-full w-full" draggable={false} title={`${altOf(i)} (opens full size)`}>
            <img
              src={url}
              alt={altOf(i)}
              loading="lazy"
              draggable={false}
              onError={() => setFailedIndexes((s) => (s.has(i) ? s : new Set(s).add(i)))}
              className="h-full w-full object-cover"
            />
          </a>
        )}
        {onDeletePhoto && (
          <button
            type="button"
            onClick={() => deleteClick(i)}
            onBlur={disarm}
            onKeyDown={(e) => {
              if (e.key === "Escape") disarm();
            }}
            aria-label={armed ? `Confirm delete photo ${i + 1}` : `Delete photo ${i + 1}`}
            title={armed ? "Press again to delete" : "Delete photo"}
            className={`absolute right-1 top-1 rounded-full bg-ele-danger-fill px-1.5 py-0.5 text-xs font-bold leading-none text-white shadow ${
              armed ? "ring-2 ring-white" : "opacity-90 hover:opacity-100"
            }`}
          >
            {armed ? "Delete?" : "x"}
          </button>
        )}
        {identify && (
          <button
            type="button"
            onClick={() => setActiveIdentify(i)}
            aria-label={`Identify part in photo ${i + 1}`}
            aria-pressed={active}
            title="Identify this part"
            className="absolute inset-x-0 bottom-0 bg-black/60 py-1 text-center font-menu text-[10px] tracking-wide text-ele-white transition hover:bg-ele-purple-accent"
          >
            Identify
          </button>
        )}
      </>
    );
    const tileCls = `relative aspect-square overflow-hidden rounded-ele border bg-ele-inset ${
      active ? "border-ele-purple-accent ring-2 ring-ele-purple-accent" : "border-ele-purple-accent"
    }`;
    return sortable ? (
      <SortablePhotoTile key={i} id={`p${i}`} className={tileCls} justDragged={justDragged}>
        {tileInner}
      </SortablePhotoTile>
    ) : (
      <div key={i} className={tileCls}>
        {tileInner}
      </div>
    );
  });

  const grid = <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6">{tiles}</div>;

  return (
    <div className="mt-5">
      {label !== "" && <h3 className="mb-2 font-heading text-sm uppercase tracking-wide text-ele-blue">{label}</h3>}
      {count <= 0 ? (
        <p className="text-sm text-ele-body opacity-70">{emptyText}</p>
      ) : (
        <>
          {sortable ? (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd} onDragCancel={markDragged}>
              <SortableContext items={Array.from({ length: count }, (_, i) => `p${i}`)} strategy={rectSortingStrategy}>
                {grid}
              </SortableContext>
            </DndContext>
          ) : (
            grid
          )}
          {sortable && <p className="mt-1 text-xs text-ele-body opacity-60">Drag a photo to change the order. The order is saved and used on the PDF.</p>}
          {identify && activeIdentify !== null && (
            <PartIdentify
              key={`${identify.source}-${identify.id}-${activeIdentify}`}
              source={identify.source}
              id={identify.id}
              index={activeIdentify}
              onClose={() => setActiveIdentify(null)}
            />
          )}
        </>
      )}
    </div>
  );
}

// One sortable tile. Ids are the tiles' current indices ("p0".."pN"): a drop maps back to the
// full permutation, the caller persists it, and the parent bumps `version` so every tile
// refetches by its NEW index (photos are index-addressed through the proxy).
function SortablePhotoTile({
  id,
  className,
  justDragged,
  children,
}: {
  id: string;
  className: string;
  justDragged: React.MutableRefObject<boolean>;
  children: React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={{ transform: CSS.Transform.toString(transform), transition, ...(isDragging ? { zIndex: 10, opacity: 0.85 } : {}) }}
      onClickCapture={(e) => {
        // The click a completed drag leaves behind must not open the photo / arm its delete.
        if (justDragged.current) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
      className={`${className} touch-manipulation ${isDragging ? "ring-2 ring-ele-green" : ""}`}
    >
      {children}
    </div>
  );
}
